/*
 * Homepage "Never miss it." Rewind section (sections/soulog-home-rewind.liquid; styles in assets/soulog-home-rewind.css)
 *
 * Interaction (2026-09-26, user's second verbal spec; Figma website node 1028:338 / dialog node 1032:1988):
 * The bottom icons are a progress bar (2026-09-27 user): once a step is reached it stays lit, so later steps add to the lit ones.
 *   intro         entering the section plays the intro video; the 1st icon at the bottom is lit;
 *   ask           after the intro finishes → "Before this meeting, Rewind was… Off / Enabled" pops up mid-stage; the 1st and 2nd icons are lit;
 *   enabled       choose Enabled → play the Enabled video (1st + 2nd still lit); EARLY_LIGHT_S before it ends all icons light up
 *                 (the video fades to black at the end) → when done, go to enabled-done (all lit);
 *   off           choose Off → play the Off video → when done, go to nudge; the last icon is not lit (stays on the 2nd).
 *   nudge         (2026-09-27 user: Off vs Enabled is the contrast we want, don't let people stop after Off) stays on the Off video's
 *                 last frame; the top-right button group retracts into a dot (DOCK_MS), the dot flies to mid-stage and expands into a card
 *                 styled like the choice card (data-arrive, ARRIVE_MS): "Replay" + a primary button pointing to Enabled. No countdown, no loop — it waits.
 *                 Enabled button = the card shrinks into a dot and flies back top-right (same data-morph as the choice card) while Enabled plays.
 *                 Throughout the Off branch the top-right "other scenario" button swaps to an Enabled-pointing label (dock[data-branch=off], CSS).
 * The videos themselves don't loop (no loop attribute). Loop (2026-09-27 user): as soon as a branch finishes (DONE_HOLD_MS = 0, no pause),
 * run the same retract animation as Replay and restart from the intro.
 * Only when autoplay is on — with autoplay off / reduce motion the flow never starts playing by itself, so it stops at done.
 *
 * After choosing (user's third version, 2026-09-26): the choice card shrinks into a dot and slides to the stage's top-right corner (CSS animation data-morph, duration MORPH_MS),
 * and once it lands it expands there into "Replay / View the other scenario" (data-rw-dock). Videos don't wait for the animation; they start playing immediately on click.
 *   Replay        (fourth version) the button group retracts back into a dot (DOCK_MS), the dot flies back to mid-stage and disappears (RETURN_MS, data-return),
 *                 while restarting from the intro video; after the intro finishes the choice card pops up as usual, and the user can choose again;
 *   Other scenario  toggles Enabled ↔ Off; icon rules unchanged.
 *
 * A segment with no video uploaded: the stage stays empty as a placeholder, timed by the "placeholder duration", and the flow proceeds — the whole chain can be demoed even before assets arrive.
 * Autoplay off / the OS has "reduce motion" on: skip the intro and stop directly at the dialog, waiting for the user to choose.
 *
 * Actually running = the current phase is a playing phase && the section is in the viewport && the tab is visible && nothing is selected in the editor — see isRunning().
 * "In the viewport" (2026-09-28 user: start only once the module has scrolled into place): the stage must be ≥ PLAY_AT visible
 *   (measured against min(stage height, viewport height), so a stage taller than the viewport can still pass — see tall-stage-kills-io-threshold)
 *   before playback starts; it pauses once below PAUSE_BELOW (hysteresis, so small scroll jitter doesn't stop/start it).
 *   Once the stage has left the viewport entirely, the flow resets to the first frame of the intro, so coming back starts over —
 *   except while waiting for the user to choose (ask / nudge), which is kept as is (see resetOnLeave()).
 * Loading: every <video> starts at preload="none". Before the section shows its first pixel (the "near" observer, rootMargin 0) the intro is capped
 * at metadata and the branches stay at none; once it shows, the intro is raised to auto and the two branches to metadata, and whichever is chosen is raised to auto.
 * preload only ever goes up. (2026-09-27: raising the intro to auto in connectedCallback made mobile visitors download 4.9MB on page load
 * while the section was still two screens down.) Posters: a srcset <img> under each video in the markup (not set here — waiting for this deferred script left them blank on first visit, 2026-09-28).
 * Mobile source swapping is done beforehand by the global soulog-mobile-video-src.js (included with defer in theme.liquid).
 *
 * The pure logic lives in static methods on the class, called directly by unit tests; see tests/unit/soulog-home-rewind.test.mjs.
 */
if (!customElements.get("soulog-home-rewind")) {
	const DEFAULT_IMAGE_MS = 5000;
	const MIN_IMAGE_MS = 1000;
	const PLAYING = ["intro", "enabled", "off"];
	/** Re-park the demo cursor after a card's pop-in (0.35s transform in soulog-home-rewind.css) so it lands on the settled button */
	const GHOST_SETTLE_MS = 400;
	// Matches the duration of the shr-ask-to-dot animation in soulog-home-rewind.css
	const MORPH_MS = 900;
	// Matches the CSS: the clip-path transition for .shr__dock retracting / the shr-dot-home fly-back animation
	const DOCK_MS = 450;
	const RETURN_MS = 600;
	// Hold on the done state before looping back to the intro. 0 = replay right away (2026-09-27 user: "视频最后不要停顿");
	// the last icon is still seen lit because it lights EARLY_LIGHT_S before the Enabled video ends
	const DONE_HOLD_MS = 0;
	// Light the last icon this long before the Enabled video ends: the video fades to black over its last ~0.7s
	// (measured 2026-09-27: 9.17s long, bright until 8.4s, black by 9s), so lighting on `ended` looked late (user)
	const EARLY_LIGHT_S = 2;
	// Matches the shr-dot-to-card animation: the dot flies from the top-right to mid-stage and expands into the nudge card
	const ARRIVE_MS = 900;
	// Play gate: share of the stage (relative to min(stage height, viewport height)) that must be visible to start / below which it pauses
	const PLAY_AT = 0.9;
	const PAUSE_BELOW = 0.35;
	// Phases that wait for the user's choice: leaving the viewport keeps them instead of resetting to the intro
	const WAITING = ["ask", "nudge"];
	// Which video each phase shows on stage: during the dialog it stays on the intro's last frame
	const SLIDE_OF = {
		intro: "intro",
		ask: "intro",
		enabled: "enabled",
		"enabled-done": "enabled",
		off: "off",
		nudge: "off",
	};

	class SoulogHomeRewind extends HTMLElement {
		/**
		 * The furthest step reached in the current phase. The steps read as a progress bar (2026-09-27 user):
		 * every step up to and including this one is lit (data-active), and this one is also data-current (mobile shows only its text).
		 * The last one lights only after Enabled finishes playing
		 */
		static litStep(phase, count) {
			if (phase === "intro") return 0;
			if (phase === "enabled-done") return count - 1;
			return 1;
		}

		/** Where to go after a playing phase finishes */
		static afterEnded(phase) {
			if (phase === "intro") return "ask";
			if (phase === "off") return "nudge";
			return `${phase}-done`;
		}

		/** Is playback within EARLY_LIGHT_S of the end? Unknown duration (NaN / Infinity before metadata) = no */
		static nearEnd(current, duration) {
			return Number.isFinite(duration) && duration > 0 && duration - current <= EARLY_LIGHT_S;
		}

		/**
		 * Play gate from an IntersectionObserver entry. coverage = visible height / min(stage height, viewport height);
		 * ≥ PLAY_AT → on, not intersecting or < PAUSE_BELOW → off, in between → keep the previous state
		 */
		static playGate(entry, wasOn) {
			if (!entry.isIntersecting) return false;
			const box = entry.boundingClientRect;
			const root = entry.rootBounds;
			const full = Math.min(box.height, root ? root.height : box.height);
			const coverage = full > 0 ? entry.intersectionRect.height / full : 0;
			if (coverage >= PLAY_AT - 0.001) return true;
			if (coverage < PAUSE_BELOW) return false;
			return wasOn;
		}

		/** Where to go when the stage has fully left the viewport: null = keep (waiting for a choice / already at the start) */
		static resetOnLeave(phase, autoplay) {
			if (WAITING.includes(phase)) return null;
			// Without autoplay, the start is the dialog itself: never start playing on the user's behalf
			return autoplay ? "intro" : "ask";
		}

		static imageDuration(raw) {
			const ms = Number(raw);
			return Number.isFinite(ms) && ms >= MIN_IMAGE_MS ? ms : DEFAULT_IMAGE_MS;
		}

		connectedCallback() {
			this.slides = Array.from(this.querySelectorAll("[data-rw-slide]"));
			this.steps = Array.from(this.querySelectorAll("[data-rw-step]"));
			this.ask = this.querySelector("[data-rw-ask]");
			this.dock = this.querySelector("[data-rw-dock]");
			this.nudge = this.querySelector("[data-rw-nudge]");
			if (!this.slides.length) return;

			this.imageMs = SoulogHomeRewind.imageDuration(this.dataset.imageMs);
			this.elapsed = 0;
			this.lastTick = 0;
			this.inView = false;
			this.near = false; // has the section shown its first pixel? Before that nothing downloads in full
			this.editorHold = false;
			this.stuck = new WeakSet(); // videos that won't play: advance by the placeholder timer instead of getting stuck

			this.tick = this.tick.bind(this);
			this.onVisibility = () => this.update();
			document.addEventListener("visibilitychange", this.onVisibility);

			this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

			this.querySelectorAll("[data-rw-choice]").forEach((btn) =>
				btn.addEventListener("click", () => {
					if (this.phase !== "ask") return;
					this.go(btn.getAttribute("data-rw-choice"), { morph: true });
				}),
			);

			const replay = this.querySelector("[data-rw-replay]");
			if (replay) replay.addEventListener("click", () => this.replay());
			const other = this.querySelector("[data-rw-other]");
			if (other) other.addEventListener("click", () => this.go(this.branch() === "enabled" ? "off" : "enabled"));
			const nudgeGo = this.querySelector("[data-rw-nudge-go]");
			if (nudgeGo)
				nudgeGo.addEventListener("click", () => {
					if (this.phase === "nudge") this.go("enabled", { morph: true });
				});
			const nudgeReplay = this.querySelector("[data-rw-nudge-replay]");
			if (nudgeReplay)
				nudgeReplay.addEventListener("click", () => {
					if (this.phase === "nudge") this.go("intro");
				});
			this.setDock(false);

			this.slides.forEach((slide) => {
				const video = videoIn(slide);
				if (!video) return;
				const key = slide.getAttribute("data-rw-slide");
				// Only honor the current phase's ended: a late event from a segment we've switched away from must not derail the flow
				video.addEventListener("ended", () => {
					if (this.phase === key) this.go(SoulogHomeRewind.afterEnded(key));
				});
				video.addEventListener("error", () => this.markStuck(video));
				if (key === "enabled")
					video.addEventListener("timeupdate", () => {
						if (this.phase === "enabled" && SoulogHomeRewind.nearEnd(video.currentTime, video.duration))
							this.lightSteps(this.steps.length - 1);
					});
			});

			// Play gate: start only once the stage is (almost) fully in view, reset to the start once it has fully left — see playGate() / resetOnLeave().
			// Fine-grained thresholds only make the callback fire often enough; the decision itself is made on coverage, not on intersectionRatio
			const thresholds = [];
			for (let t = 0; t <= 20; t++) thresholds.push(t / 20);
			this.io = new IntersectionObserver(
				(entries) => {
					const entry = entries[entries.length - 1];
					if (entry.isIntersecting) this.approach();
					this.inView = SoulogHomeRewind.playGate(entry, this.inView);
					if (!entry.isIntersecting) this.leave();
					this.update();
				},
				{ threshold: thresholds },
			);
			this.io.observe(this.querySelector("[data-rw-stage]") || this);

			// Theme editor: pause when an item is selected, resume when deselected
			this.onBlockSelect = (e) => {
				if (this.steps.indexOf(e.target) < 0) return;
				this.editorHold = true;
				this.update();
			};
			this.onBlockDeselect = () => {
				this.editorHold = false;
				this.update();
			};
			document.addEventListener("shopify:block:select", this.onBlockSelect);
			document.addEventListener("shopify:block:deselect", this.onBlockDeselect);

			this.nearIo = new IntersectionObserver((entries) => {
				if (entries.some((e) => e.isIntersecting)) this.approach();
			});
			this.nearIo.observe(this);

			this.setupGhost();

			this.autoplay = this.hasAttribute("data-autoplay") && !this.reducedMotion;
			this.go(this.autoplay ? "intro" : "ask");
		}

		/**
		 * Green demo cursor (user request 2026-09-28, same as the "Try It Yourself" section; shared assets/soulog-ghost-cursor.js):
		 * while the choice card (or the Off-branch nudge card) is up, it rests on the card's primary button — "Enabled" — and sways;
		 * the pointer entering the stage merges it into a green system cursor. It re-parks whenever a card opens / closes / morphs,
		 * once more after the card's pop-in animation (0.35s) so it lands on the button's final position.
		 */
		setupGhost() {
			const stage = this.querySelector("[data-rw-stage]");
			if (!window.SoulogGhostCursor || !stage) return;
			this.ghost = new window.SoulogGhostCursor(this, {
				container: stage,
				target: () => stage.querySelector(".shr__ask[data-open]:not([data-morph]) .shr__ask-btn--primary"),
				zone: () => stage.getBoundingClientRect(),
			});
			const cards = [this.ask, this.nudge].filter(Boolean);
			this.ghostMo = new MutationObserver(() => {
				// A card closing while the pointer is over the stage: give the system cursor back
				this.ghost.check();
				this.ghost.park();
				clearTimeout(this.ghostTimer);
				this.ghostTimer = setTimeout(() => this.ghost && this.ghost.park(), GHOST_SETTLE_MS);
			});
			cards.forEach((card) =>
				this.ghostMo.observe(card, { attributes: true, attributeFilter: ["data-open", "data-morph"] }),
			);
		}

		disconnectedCallback() {
			if (!this.slides || !this.slides.length) return;
			cancelAnimationFrame(this.raf);
			clearTimeout(this.morphTimer);
			clearTimeout(this.returnTimer);
			clearTimeout(this.loopTimer);
			clearTimeout(this.arriveTimer);
			clearTimeout(this.ghostTimer);
			if (this.ghostMo) this.ghostMo.disconnect();
			if (this.ghost) this.ghost.destroy();
			this.ghost = null;
			if (this.io) this.io.disconnect();
			if (this.nearIo) this.nearIo.disconnect();
			document.removeEventListener("visibilitychange", this.onVisibility);
			document.removeEventListener("shopify:block:select", this.onBlockSelect);
			document.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
			// Editor moves / deletes the section: don't let videos keep decoding in the background
			this.slides.forEach((slide) => {
				const video = videoIn(slide);
				if (video && !video.paused) video.pause();
			});
		}

		slideOf(key) {
			return this.slides.find((s) => s.getAttribute("data-rw-slide") === key) || null;
		}

		/** The current branch (enabled / off); null before a choice is made */
		branch() {
			const key = SLIDE_OF[this.phase];
			return key === "intro" ? null : key;
		}

		setDock(open) {
			if (!this.dock) return;
			this.dock.toggleAttribute("data-open", open);
			this.dock.inert = !open;
			this.dock.setAttribute("aria-hidden", open ? "false" : "true");
		}

		/** Replay: the top-right button group retracts into a dot and flies back to the center to disappear (the post-choice animation in reverse), while restarting from the intro */
		replay() {
			clearTimeout(this.morphTimer);
			clearTimeout(this.returnTimer);
			this.setDock(false);
			if (this.ask) {
				this.ask.toggleAttribute("data-morph", false);
				this.ask.toggleAttribute("data-return", false);
			}
			this.go("intro");
			if (!this.ask || this.reducedMotion) return;
			this.returnTimer = setTimeout(() => {
				this.ask.toggleAttribute("data-return", true);
				this.returnTimer = setTimeout(() => this.ask.toggleAttribute("data-return", false), RETURN_MS);
			}, DOCK_MS);
		}

		/** The stage fully left the viewport: silently go back to the first frame (no retract / fly-back animation — nobody is watching) */
		leave() {
			const to = SoulogHomeRewind.resetOnLeave(this.phase, this.autoplay);
			if (!to) return;
			clearTimeout(this.morphTimer);
			clearTimeout(this.returnTimer);
			this.setDock(false);
			if (this.ask) {
				this.ask.toggleAttribute("data-morph", false);
				this.ask.toggleAttribute("data-return", false);
			}
			this.go(to);
		}

		isRunning() {
			return PLAYING.includes(this.phase) && this.inView && !document.hidden && !this.editorHold;
		}

		/** Switch to a phase: swap the stage visual, open/close the dialog, light the matching icon. morph = the choice card shrinks into a dot and flies to the top-right */
		go(phase, { morph = false } = {}) {
			const from = this.phase;
			this.phase = phase;
			this.elapsed = 0;
			this.lastTick = 0;
			const current = this.slideOf(SLIDE_OF[phase]);

			this.slides.forEach((slide) => {
				const on = slide === current;
				slide.toggleAttribute("data-active", on);
				const video = videoIn(slide);
				if (!video) return;
				if (!on) {
					if (!video.paused) video.pause();
				} else if (PLAYING.includes(phase)) {
					// Only the autoplayed intro waits for the section to show; branches are reached by a click (or after the intro was watched)
					raisePreload(video, this.near || phase !== "intro" ? "auto" : "metadata");
					rewind(video);
				}
			});

			// The card that flies to the top-right on morph: the choice card, or the nudge card when leaving it
			const flyer = from === "nudge" && this.nudge ? this.nudge : this.ask;
			[this.ask, this.nudge].forEach((card) => {
				if (!card) return;
				const open = phase === (card === this.nudge ? "nudge" : "ask");
				card.inert = !open;
				card.setAttribute("aria-hidden", open ? "false" : "true");
				if (morph && card === flyer && !this.reducedMotion) {
					// The card keeps data-open so it stays on screen, and data-morph takes over its shape and position; after landing it closes and the top-right buttons expand.
					// On landing only data-open is removed and data-morph stays — removing morph first would make the card fade out at its original size and flash (the CSS rule there makes it vanish instantly)
					card.toggleAttribute("data-arrive", false);
					card.toggleAttribute("data-morph", true);
					clearTimeout(this.morphTimer);
					this.morphTimer = setTimeout(() => {
						card.toggleAttribute("data-open", false);
						if (this.phase === phase) this.setDock(true);
					}, MORPH_MS);
					return;
				}
				if (open) {
					clearTimeout(this.returnTimer);
					card.toggleAttribute("data-morph", false);
					card.toggleAttribute("data-return", false);
				}
				// The nudge card opens later, after the dock has retracted (see below)
				if (card === this.nudge && open) return;
				card.toggleAttribute("data-open", open);
				if (!open) card.toggleAttribute("data-arrive", false);
			});
			if (morph && (!flyer || this.reducedMotion)) this.setDock(true);

			// Off finished: the top-right button group retracts into a dot, the dot flies to mid-stage and expands into the nudge card
			clearTimeout(this.arriveTimer);
			if (phase === "nudge" && this.nudge) {
				this.setDock(false);
				const nudge = this.nudge;
				const open = () => {
					if (this.phase !== "nudge") return;
					nudge.toggleAttribute("data-open", true);
					if (this.reducedMotion) return;
					nudge.toggleAttribute("data-arrive", true);
					this.arriveTimer = setTimeout(() => nudge.toggleAttribute("data-arrive", false), ARRIVE_MS);
				};
				if (this.reducedMotion) open();
				else this.arriveTimer = setTimeout(open, DOCK_MS);
			}

			// Loop: after a branch finishes, replay from the intro (after DONE_HOLD_MS). The phase check guards against a stale timer
			// (the user clicked Replay / Other during the hold) dragging the flow back
			clearTimeout(this.loopTimer);
			if (this.autoplay && phase.endsWith("-done")) {
				this.loopTimer = setTimeout(() => {
					if (this.phase === phase) this.replay();
				}, DONE_HOLD_MS);
			}

			if (this.dock) this.dock.setAttribute("data-branch", this.branch() || "");

			this.lightSteps(SoulogHomeRewind.litStep(phase, this.steps.length));
			this.update();
		}

		/** Light every step up to and including `lit` (progress bar); `lit` is also marked data-current */
		lightSteps(lit) {
			this.steps.forEach((step, n) => {
				step.toggleAttribute("data-active", n <= lit);
				step.toggleAttribute("data-current", n === lit);
			});
		}

		/** The section showed its first pixel: the current segment downloads in full, and both branches fetch metadata so playback can start as soon as the user chooses */
		approach() {
			if (this.near) return;
			this.near = true;
			if (this.nearIo) this.nearIo.disconnect();
			const current = videoIn(this.slideOf(SLIDE_OF[this.phase]));
			if (current && PLAYING.includes(this.phase)) raisePreload(current, "auto");
			["enabled", "off"].forEach((key) => {
				const video = videoIn(this.slideOf(key));
				if (video) raisePreload(video, "metadata");
			});
		}

		/** Call once after any state change: keep video / timing in sync with isRunning() */
		update() {
			const running = this.isRunning();
			const video = videoIn(this.slideOf(SLIDE_OF[this.phase]));

			if (video && !this.stuck.has(video) && PLAYING.includes(this.phase)) {
				if (running && video.paused && !video.ended) {
					const played = video.play && video.play();
					// muted + playsinline is rarely rejected; if it really is, treat it as unplayable and advance by the placeholder timer
					if (played && typeof played.catch === "function") played.catch(() => this.markStuck(video));
				} else if (!running && !video.paused) {
					video.pause();
				}
			}

			cancelAnimationFrame(this.raf);
			this.lastTick = 0;
			// Only segments with no video / an unplayable video need their own timer; segments with video rely on the ended event
			const timed = !video || this.stuck.has(video);
			if (running && timed) this.raf = requestAnimationFrame(this.tick);
		}

		tick(now) {
			if (!this.isRunning()) return;
			if (this.lastTick) this.elapsed += now - this.lastTick;
			this.lastTick = now;
			if (this.phase === "enabled" && SoulogHomeRewind.nearEnd(this.elapsed / 1000, this.imageMs / 1000))
				this.lightSteps(this.steps.length - 1);
			if (this.elapsed >= this.imageMs) {
				this.go(SoulogHomeRewind.afterEnded(this.phase));
				return;
			}
			this.raf = requestAnimationFrame(this.tick);
		}

		markStuck(video) {
			if (this.stuck.has(video)) return;
			this.stuck.add(video);
			this.update();
		}
	}

	function videoIn(slide) {
		return slide && slide.querySelector ? slide.querySelector("[data-rw-video]") : null;
	}

	/** Rewind to the start: a video whose turn comes again plays from the beginning. If there's no seekable timeline yet, just skip — the subsequent play starts from the beginning anyway */
	const PRELOAD_RANK = { none: 0, metadata: 1, auto: 2 };
	/** preload only goes up: never pull a segment that is already downloading back to metadata */
	function raisePreload(video, want) {
		if ((PRELOAD_RANK[video.preload] || 0) < PRELOAD_RANK[want]) video.preload = want;
	}

	function rewind(video) {
		if (!video.currentTime) return;
		try {
			video.currentTime = 0;
		} catch (_) {
			/* noop */
		}
	}

	customElements.define("soulog-home-rewind", SoulogHomeRewind);
}
